import { useState } from "react";
import "./App.css";
import ecfrImage from "./assets/ecfr.png";
import honorFlightImage from "./assets/honorflight.png";

function App() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  const projects = [
    {
      title: "Electronic Code of Federal Regulations Visualization",
      category: "Personal Project",
      description:
        "Built a full-stack web application using React, D3.js, and Node.js to visualize large datasets of federal regulations. Designed interactive charts to improve data exploration and usability.",
      tech: "React, D3.js, Node.js, Express, MySQL, Docker",
      github: null,
      image: ecfrImage,
    },
    {
      title: "BrickDatabase",
      category: "Academic Project",
      description:
        "Full-stack web application integrating Reddit data using React, Node.js, and MongoDB. Designed UI/UX and built reusable components to improve consistency and user experience.",
      tech: "React, Node.js, Express, MongoDB, HTML5, CSS3",
      github: "https://github.com/BrickDatabase/Brick-Subreddit",
    },
    {
      title: "Rochester Honor Flight",
      category: "Academic Project",
      description:
        "Developed frontend components using HTML, CSS, JavaScript, and Handlebars to build responsive web pages. Collaborated with backend developers and designers to integrate REST APIs and implemented user-friendly interfaces.",
      tech: "JavaScript, HTML5, CSS3, Handlebars, REST APIs",
      github: "https://github.com/SakuraAmy/srdevkings",
      image: honorFlightImage,
    },
  ];

  const experience = [
    {
      title: "PNC - Redesign Mobile App Initiative",
      date: "May 2023 – Oct 2024",
      subtitle:
        "Mobile banking application modernization using Micro-UI architecture",
      description:
        "Developed modular Micro-UI components in a Swift-based mobile application to improve scalability and maintainability. Collaborated with cross-functional teams to deliver new features and streamline integration across the application.",
    },
    {
      title: "PNC – Accessibility Improvements",
      date: "Jan 2021 – May 2021",
      subtitle:
        "Enhancing accessibility for a large-scale mobile banking application",
      description:
        "Implemented accessibility features including VoiceOver support, dynamic text scaling, and contrast adjustments. Worked closely with QA and design teams to improve usability and ensure inclusive user experiences.",
    },
  ];

  return (
    <div className="app">
      <header className="hero">
        <div className="petals petals-1">🌸</div>
        <div className="petals petals-2">🌸</div>
        <div className="petals petals-3">🌸</div>

        <nav className="navbar">
          <h2 className="logo">Amina Riley</h2>

          <div className="nav-links">
            <a href="#about">About</a>
            <a href="#skills">Skills</a>
            <a href="#projects">Projects</a>
            <a href="#experience">Experience</a>
            <button
              type="button"
              className="nav-contact-btn"
              onClick={() => setShowContactModal(true)}
            >
              Contact
            </button>
          </div>
        </nav>

        <div className="hero-content">
          <p className="intro">Hello, my name is</p>
          <h1>Amina Riley</h1>
          <h2>Software Engineer | Frontend & Mobile Developer</h2>

          <p className="summary">
            I design and develop responsive web and mobile applications with a
            strong focus on accessibility, clean interfaces, and scalable
            architecture.
          </p>

          <div className="hero-buttons">
            <a href="#projects" className="btn primary">
              View Projects
            </a>

            <button
              type="button"
              className="btn secondary"
              onClick={() => setShowContactModal(true)}
            >
              Contact Me
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <section id="about" className="section">
          <div className="section-header">
            <span className="section-flower">🌸</span>
            <h2>About Me</h2>
          </div>

          <div className="about-content">
            <p>
              I&apos;m a software engineer with 4+ years of experience developing
              mobile and web applications. My work focuses on frontend
              development, accessibility, and creating intuitive user
              experiences.
            </p>

            <p>
              I&apos;ve worked on agile teams building scalable applications
              using Swift, JavaScript, and modern development tools. I enjoy
              turning complex problems into simple, reliable, and user-centered
              solutions.
            </p>
          </div>
        </section>

        <section id="skills" className="section">
          <div className="section-header">
            <span className="section-flower">🌸</span>
            <h2>Skills</h2>
          </div>

          <div className="skills-grid">
            <span>React</span>
            <span>JavaScript</span>
            <span>HTML5</span>
            <span>CSS3</span>
            <span>Swift</span>
            <span>iOS Development</span>
            <span>Node.js</span>
            <span>REST APIs</span>
            <span>Accessibility (WCAG)</span>
            <span>Responsive Design</span>
            <span>Git/GitHub</span>
            <span>Agile Development</span>
          </div>
        </section>

        <section id="projects" className="section">
          <div className="section-header">
            <span className="section-flower">🌸</span>
            <h2>Projects</h2>
          </div>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <div className="project-card" key={index}>
                <div className="project-media">
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={`${project.title} preview`}
                      className="project-image"
                    />
                  ) : (
                    <div className="project-image project-placeholder">
                      <span className="placeholder-icon">🌸</span>
                      <span className="placeholder-text">
                        {project.title} System
                      </span>
                    </div>
                  )}
                </div>

                <div className="project-body">
                  <h3>{project.title}</h3>
                  <p className="project-category">{project.category}</p>
                  <p className="project-description">{project.description}</p>

                  <p className="tech">
                    <strong>Tech:</strong> {project.tech}
                  </p>
                </div>

                <div className="project-links">
                  {/* if github exists, do add link */}
                  {project.github && (
                    <a href={project.github} target="_blank" rel="noreferrer">
                      GitHub
                    </a>
                  )}

                  {project.image && (
                    <button
                      type="button"
                      className="screenshot-btn"
                      onClick={() => setSelectedImage(project.image)}
                    >
                      View Screenshot
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="experience" className="section">
          <div className="section-header">
            <span className="section-flower">🌸</span>
            <h2>Professional Experience</h2>
          </div>

          <div className="experience-list">
            {experience.map((item, index) => (
              <div className="experience-card" key={index}>
                <h3 className="experience-title">{item.title}</h3>
                <p className="experience-date">{item.date}</p>
                <p className="experience-subtitle">{item.subtitle}</p>
                <p className="experience-description">{item.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {selectedImage && (
        <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
          <div
            className="modal-content image-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>

            <img src={selectedImage} alt="Project Screenshot" />
          </div>
        </div>
      )}

      {showContactModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowContactModal(false)}
        >
          <div
            className="modal-content contact-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={() => setShowContactModal(false)}
            >
              ✕
            </button>

            <div className="contact-modal-header">
              <span className="contact-modal-flower">🌸</span>
              <h2>Contact Me</h2>
            </div>

            <p className="contact-modal-text">
              Thanks for visiting my portfolio. Feel free to reach out for
              opportunities, collaborations, or questions.
            </p>

            <div className="contact-info-list">
              <div className="contact-info-item">
                <span className="contact-label">Email</span>
                <a href="mailto:amina.m1996@gmail.com">amina.m1996@gmail.com</a>
              </div>

              <div className="contact-info-item">
                <span className="contact-label">LinkedIn</span>
                <a
                  href="https://linkedin.com/in/amina-riley"
                  target="_blank"
                  rel="noreferrer"
                >
                  linkedin.com/in/amina-riley
                </a>
              </div>

              <div className="contact-info-item">
                <span className="contact-label">GitHub</span>
                <a
                  href="https://github.com/SakuraAmy"
                  target="_blank"
                  rel="noreferrer"
                >
                  github.com/SakuraAmy
                </a>
              </div>
            </div>

            <div className="contact-modal-actions">
              <a
                href="mailto:amina.m1996@gmail.com"
                className="contact-action-btn primary-contact"
              >
                Email Me
              </a>

              <a
                href="https://linkedin.com/in/amina-riley"
                target="_blank"
                rel="noreferrer"
                className="contact-action-btn secondary-contact"
              >
                View LinkedIn
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;